import type { ServerMonitoring as Monitoring, ServerSummary } from "@woven/contracts";
import { useEffect, useMemo, useState } from "react";
import { Tooltip } from "../../components/ui/Tooltip";
import { useControlApiClient } from "../../lib/hooks";

export function ServerMonitoring({ server }: { server: ServerSummary }) {
  if (
    !server.connection?.endpoint ||
    server.connection.tokenStatus !== "active" ||
    !server.generation ||
    server.lifecycle?.deleteIntent ||
    !server.connection.namespaceId ||
    !server.connection.sessionId
  ) {
    return <CapacitySummary data={null} />;
  }
  return <SessionCapacity server={server} />;
}

function CapacitySummary({
  data,
  loading = false,
}: {
  data: Monitoring | null;
  loading?: boolean;
}) {
  const admission = data?.admission;
  const unavailable = loading ? "Loading…" : "Unavailable";
  return (
    <section aria-label="Server capacity" className="server-admission">
      <dl className="server-card-metrics">
        <div>
          <dt>
            Active / allocated CCU{" "}
            <Tooltip label="About live server CCU">
              Current concurrent occupancy / effective session admission limit, not account capacity
              (Hz × allocated CCU).
            </Tooltip>
          </dt>
          <dd>
            {admission
              ? `${admission.activeCCU} / ${admission.effectiveAllocatedCCU}`
              : unavailable}
          </dd>
        </div>
        {data?.tickRateHz !== undefined && (
          <div>
            <dt>
              Live publish limit{" "}
              <Tooltip label="About live publish limit">
                The node's aggregate publish-traffic limit per connected client per second across
                channels and spaces. It does not schedule simulation ticks.
              </Tooltip>
            </dt>
            <dd>
              {data.tickRateHz} Hz · {data.tickRateHz} publishes/client/second
            </dd>
          </div>
        )}
        <div>
          <dt>In Queue</dt>
          <dd>{admission ? admission.queueDepth : unavailable}</dd>
        </div>
      </dl>
      {admission && (
        <progress
          className="settings-ccu-bar"
          aria-label="Active / allocated CCU"
          aria-valuetext={`${admission.activeCCU} active of ${admission.effectiveAllocatedCCU} allocated CCU`}
          max={1}
          value={
            admission.effectiveAllocatedCCU > 0
              ? Math.min(admission.activeCCU / admission.effectiveAllocatedCCU, 1)
              : admission.activeCCU > 0
                ? 1
                : 0
          }
        />
      )}
      {admission?.pendingTarget != null && (
        <p className="form-note">
          CCU change pending: target {admission.pendingTarget}. Capacity may remain reserved while
          connections drain; no immediate disconnection is promised.
        </p>
      )}
      {loading && (
        <span role="status" className="form-note">
          Loading server capacity…
        </span>
      )}
    </section>
  );
}

function SessionCapacity({ server }: { server: ServerSummary }) {
  const client = useControlApiClient();
  const scope = useMemo(
    () => ({
      client,
      id: server.id,
      generation: server.generation,
      endpoint: server.connection?.endpoint,
      namespaceId: server.connection?.namespaceId,
      sessionId: server.connection?.sessionId,
    }),
    [
      client,
      server.id,
      server.generation,
      server.connection?.endpoint,
      server.connection?.namespaceId,
      server.connection?.sessionId,
    ]
  );
  const [snapshot, setSnapshot] = useState<{
    scope: typeof scope;
    data: Monitoring | null;
  } | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    if (!scope.generation) return () => controller.abort();
    client.getServerMonitoring(scope.id, { generation: scope.generation }, controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) {
          const matches =
            data.namespaceId === scope.namespaceId && data.sessionId === scope.sessionId;
          setSnapshot({ scope, data: matches ? data : null });
        }
      },
      () => {
        if (!controller.signal.aborted) setSnapshot({ scope, data: null });
      }
    );
    return () => controller.abort();
  }, [client, scope]);
  const current = snapshot?.scope === scope ? snapshot : null;
  return <CapacitySummary data={current?.data ?? null} loading={!current} />;
}
